// Responsive data card component for mobile view
// CREATED: Generic card component that displays data in a vertical layout optimized for mobile
// Used by: UserManagement, SiteManagement, ServiceManagement, TemplateLibrary
// Updated: Increased action button touch target to 48px (WCAG 2.1 Level AA compliant)
// Updated: Badge prop now accepts either {label, colorClass} object or ReactNode for custom badge components

import { ReactNode } from 'react';

export interface ResponsiveDataCardProps {
  primaryLabel: string;
  secondaryLabel?: string;
  badge?: {
    label: string;
    colorClass: string;
  } | ReactNode;
  metadata?: Array<{
    icon: ReactNode;
    value: string;
  }>;
  action?: {
    label: string;
    icon: ReactNode;
    onClick: () => void;
  };
}

export function ResponsiveDataCard({
  primaryLabel,
  secondaryLabel,
  badge,
  metadata,
  action
}: ResponsiveDataCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      {/* Primary label */}
      <h3 className="font-semibold text-gray-900 text-base mb-1 line-clamp-2">
        {primaryLabel}
      </h3>

      {/* Secondary label and badge row */}
      {(secondaryLabel || badge) && (
        <div className="flex items-center justify-between mb-3">
          {secondaryLabel && (
            <span className="text-sm text-gray-600 truncate">
              {secondaryLabel}
            </span>
          )}
          {badge && (
            typeof badge === 'object' && 'label' in badge ? (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.colorClass}`}>
                {badge.label}
              </span>
            ) : (
              badge
            )
          )}
        </div>
      )}

      {/* Metadata grid */}
      {metadata && metadata.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-3">
          {metadata.map((item, index) => (
            <div key={index} className="flex items-center gap-1.5 text-sm text-gray-600">
              {item.icon}
              <span>{item.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action button */}
      {action && (
        <button
          onClick={action.onClick}
          className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg text-sm font-medium transition-colors"
          style={{ minHeight: '48px' }}
        >
          {action.icon}
          {action.label}
        </button>
      )}
    </div>
  );
}
